import type { WidgetComponentProps } from "../types";
import { useSummaryQuery } from "../useSummaryQuery";

function DonutStatus({ ready, failed }: { ready: number; failed: number }) {
  const total = ready + failed;
  const pct = total === 0 ? 1 : ready / total;
  const deg = pct * 360;
  return (
    <div className="sys-donut">
      <div
        className="sys-donut__ring"
        style={{
          background: `conic-gradient(#4ADE80 ${deg}deg, #EF4444 ${deg}deg)`,
        }}
      >
        <div className="sys-donut__inner">
          <span className="sys-donut__value">{(pct * 100).toFixed(1)}%</span>
          <span className="sys-donut__label">Success</span>
        </div>
      </div>
    </div>
  );
}

export function SystemStatusWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  if (isLoading) return <p className="widget-empty">Loading…</p>;
  const totals = data?.totals;
  if (!totals) return <p className="widget-empty">No data.</p>;

  const failed = totals.failed_flights ?? 0;
  const ready = totals.ready_flights ?? 0;

  if (sizeVariant === "s") {
    return (
      <div className="sys-status sys-status--s">
        <div className="sys-status__row">
          <span className="sys-status__label">Ready</span>
          <span className="sys-status__val sys-status__val--ok">{ready}</span>
        </div>
        <div className="sys-status__row">
          <span className="sys-status__label">Failed</span>
          <span
            className={`sys-status__val ${failed > 0 ? "sys-status__val--danger" : ""}`}
          >
            {failed}
          </span>
        </div>
      </div>
    );
  }

  if (sizeVariant === "l") {
    return (
      <div className="sys-status sys-status--l">
        <div className="sys-status__left">
          <DonutStatus ready={ready} failed={failed} />
          <p className="sys-status__caption">
            {ready} ready / {ready + failed} total
          </p>
        </div>
        <div className="sys-status__right">
          <div className="sys-status__row">
            <span className="sys-status__label">Ready flights</span>
            <span className="sys-status__val sys-status__val--ok">{ready}</span>
          </div>
          <div className="sys-status__row">
            <span className="sys-status__label">Failed imports</span>
            <span
              className={`sys-status__val ${failed > 0 ? "sys-status__val--danger" : ""}`}
            >
              {failed}
            </span>
          </div>
          <div className="sys-status__row">
            <span className="sys-status__label">Missions tracked</span>
            <span className="sys-status__val">{totals.mission_count}</span>
          </div>
          <div className="sys-status__row">
            <span className="sys-status__label">Maps indexed</span>
            <span className="sys-status__val">{totals.map_count}</span>
          </div>
          <div className="sys-status__row">
            <span className="sys-status__label">Distance flown</span>
            <span className="sys-status__val">
              {totals.total_distance_km.toFixed(1)} km
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sys-status sys-status--m">
      <DonutStatus ready={ready} failed={failed} />
      <div className="sys-status__rows">
        <div className="sys-status__row">
          <span className="sys-status__label">Ready</span>
          <span className="sys-status__val sys-status__val--ok">{ready}</span>
        </div>
        <div className="sys-status__row">
          <span className="sys-status__label">Failed</span>
          <span
            className={`sys-status__val ${failed > 0 ? "sys-status__val--danger" : ""}`}
          >
            {failed}
          </span>
        </div>
        <div className="sys-status__row">
          <span className="sys-status__label">Missions</span>
          <span className="sys-status__val">{totals.mission_count}</span>
        </div>
      </div>
    </div>
  );
}
